import type { ListBuilderConfiguration } from '../types';

/**
 * Shared payment method value mapping for icons
 */
const PAYMENT_METHOD_ICON_MAPPING = [
	{ value: 'Credit Card', iconName: 'credit-card' },
	{ value: 'Credit Card Reader', iconName: 'credit-card' },
	{ value: 'Check', iconName: 'money-check-dollar' },
	{ value: 'Cash', iconName: 'money-bill' },
	{ value: 'ACH', iconName: 'building' },
	{ value: 'Company Credit', iconName: 'building' },
];

/**
 * Shared transaction status value mapping for badges
 */
const TRANSACTION_STATUS_MAPPING = [
	{
		color: { bg: '#059669', text: '#ffffff' },
		value: 'succeeded',
		displayText: 'Succeeded',
	},
	{
		color: { bg: '#059669', text: '#ffffff' },
		value: 'completed',
		displayText: 'Completed',
	},
	{
		color: { bg: '#d97706', text: '#ffffff' },
		value: 'pending',
		displayText: 'Pending',
	},
	{
		color: { bg: '#d97706', text: '#ffffff' },
		value: 'processing',
		displayText: 'Processing',
	},
	{
		color: { bg: '#dc2626', text: '#ffffff' },
		value: 'failed',
		displayText: 'Failed',
	},
	{
		color: { bg: '#dc2626', text: '#ffffff' },
		value: 'error',
		displayText: 'Error',
	},
	{
		color: { bg: '#6b7280', text: '#ffffff' },
		value: 'cancelled',
		displayText: 'Cancelled',
	},
	{
		color: { bg: '#6b7280', text: '#ffffff' },
		value: 'voided',
		displayText: 'Voided',
	},
];

/**
 * Transaction Order Embedded list configuration
 * Shows transactions embedded in an order view
 * Features conditional data configs, calculated fields, value mappings, and visibility callbacks
 * Converted from JSON to TypeScript for type safety
 */
export const TRANSACTION_ORDER_EMBEDDED_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'order-embedded-transaction-all',
			cacheTTL: 300000,
			name: 'All Transactions',
			type: 'default',
			active: true,
			sortOrder: 0,
			columns: [
				{
					id: 'transaction-id-origin',
					fields: [
						{
							id: 'transaction-id-field',
							link: {
								type: 'object',
								enabled: true,
								idField: '_id',
								objectTable: 'transaction_summary',
							},
							dataConfig: {
								type: 'field',
								field: {
									fieldName: '_id',
									objectTable: 'transaction_summary',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'static',
									staticColor: {
										text: '#3b82f6',
									},
								},
							},
							displayStyle: 'text',
						},
						{
							id: 'line-break-1',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'line_break',
						},
						{
							id: 'transaction-origin-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'm_payment_origin',
									objectTable: 'transaction_summary',
								},
								defaultValue: 'CRM',
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'theme',
									themeColor: 'textSecondary',
								},
							},
							displayStyle: 'text',
						},
					],
					headers: [
						{ id: 'h1-transaction-id', text: 'Transaction ID' },
						{ id: 'h1-origin', text: 'Origin' },
					],
					minWidth: 150,
					sortable: true,
					alignment: 'left',
					sortFields: [
						{
							direction: 'DESC',
							fieldName: '_id',
							objectTable: 'transaction_summary',
						},
					],
					widthConfig: {
						mode: 'flexible',
						preset: 'small',
					},
					verticalAlignment: 'top',
				},
				{
					id: 'payment-method',
					fields: [
						{
							id: 'payment-method-icon-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'm_payment_payment_method_type',
									objectTable: 'transaction_summary',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'value-mapping',
								},
								valueMapping: PAYMENT_METHOD_ICON_MAPPING,
							},
							displayStyle: 'icon',
						},
						{
							id: 'payment-method-subtype-field',
							dataConfig: {
								type: 'conditional',
								conditions: [
									{
										field: 'm_payment_payment_method_type',
										value: 'check',
										operator: 'equals',
										dataConfig: {
											type: 'field',
											field: {
												fieldName: 'm_payment_checkNumber',
												objectTable: 'transaction_summary',
											},
										},
									},
									{
										field: 'm_payment_payment_method_type',
										value: 'cash',
										operator: 'equals',
										dataConfig: {
											type: 'custom_text',
											customText: '',
										},
									},
								],
								defaultDataConfig: {
									type: 'template',
									template:
										'{m_payment_payment_method_subtype} - {m_payment_payment_method_lastFour}',
									templateFields: [
										{
											fieldName: 'm_payment_payment_method_subtype',
											objectTable: 'transaction_summary',
										},
										{
											fieldName: 'm_payment_payment_method_lastFour',
											objectTable: 'transaction_summary',
										},
									],
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'theme',
									themeColor: 'textPrimary',
								},
							},
							displayStyle: 'text',
						},
						{
							id: 'line-break-3',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'line_break',
						},
						{
							id: 'payment-method-type-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'm_payment_payment_method_type',
									objectTable: 'transaction_summary',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'theme',
									themeColor: 'textSecondary',
								},
							},
							displayStyle: 'text',
						},
						{
							id: 'line-break-4',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'line_break',
						},
						{
							id: 'payment-method-name-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'm_payment_payment_method_name',
									objectTable: 'transaction_summary',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'theme',
									themeColor: 'textSecondary',
								},
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h3', text: 'Payment Method' }],
					minWidth: 180,
					sortable: false,
					alignment: 'left',
					widthConfig: {
						mode: 'flexible',
						preset: 'large',
					},
					verticalAlignment: 'top',
				},
				{
					id: 'transaction-amounts',
					fields: [
						{
							id: 'amount-paid-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'm_sm_payment_amount',
									objectTable: 'transaction_summary',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'theme',
									themeColor: 'textPrimary',
								},
							},
							displayStyle: 'currency',
						},
						{
							id: 'line-break-5',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'line_break',
						},
						{
							id: 'net-amount-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'm_payment_netAmount',
									objectTable: 'transaction_summary',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'theme',
									themeColor: 'textSecondary',
								},
							},
							displayStyle: 'currency',
						},
						{
							id: 'line-break-6',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'line_break',
						},
						{
							id: 'processing-fee-field',
							// PD-15077 — the fee in dollars, computed server-side from
							// the transaction's rate and unit. This was a subtraction
							// of net amount from amount paid, which reads zero on a
							// capture (the two are equal) and a flat rate on an
							// authorization, so it reported no fee on payments that
							// carried one.
							dataConfig: {
								type: 'field',
								field: { fieldName: 'm_payment_feeCalculated' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'static',
									staticColor: {
										text: '#6b7280',
									},
								},
							},
							displayStyle: 'currency',
						},
						{
							id: 'line-break-refunded',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'line_break',
						},
						{
							id: 'refunded-amount-field',
							// PD-15091 — money actually returned on this payment
							// (succeeded refunds only; declined attempts are flagged
							// under the status column).
							dataConfig: {
								type: 'field',
								field: { fieldName: 'refundedAmount' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'conditional',
									rules: [
										{
											field: { fieldName: 'refundedAmount' },
											operator: 'not_equals',
											value: 0,
											color: { text: '#dc2626' },
										},
									],
								},
							},
							displayStyle: 'currency',
						},
					],
					headers: [
						{ id: 'h4-amount-paid', text: 'Amount Paid' },
						{ id: 'h4-net-amount', text: 'Net Amount' },
						{ id: 'h4-processing-fee', text: 'Processing Fee' },
						{ id: 'h4-refunded', text: 'Refunded' },
					],
					minWidth: 150,
					sortable: true,
					alignment: 'right',
					sortFields: [
						{
							direction: 'DESC',
							fieldName: 'm_sm_payment_amount',
							objectTable: 'transaction_summary',
						},
					],
					widthConfig: {
						mode: 'flexible',
						preset: 'medium',
					},
					verticalAlignment: 'top',
				},
				{
					id: 'transaction-dates',
					fields: [
						{
							id: 'sale-date-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'm_payment_datetime',
									objectTable: 'transaction_summary',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'theme',
									themeColor: 'textPrimary',
								},
								formatOptions: {
									dateFormat: 'datetime-medium',
								},
							},
							displayStyle: 'date',
						},
						{
							id: 'line-break-7',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'line_break',
						},
						{
							id: 'refund-date-field',
							dataConfig: {
								type: 'custom_text',
								customText: '-',
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'theme',
									themeColor: 'textSecondary',
								},
							},
							displayStyle: 'text',
						},
					],
					headers: [
						{ id: 'h5-sale-date', text: 'Sale Date' },
						{ id: 'h5-refund-date', text: 'Refund Date' },
					],
					minWidth: 160,
					sortable: true,
					alignment: 'left',
					sortFields: [
						{
							direction: 'DESC',
							fieldName: 'm_payment_datetime',
							objectTable: 'transaction_summary',
						},
					],
					widthConfig: {
						mode: 'flexible',
						preset: 'medium',
					},
					verticalAlignment: 'top',
				},
				{
					id: 'status',
					fields: [
						{
							id: 'status-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'm_payment_status',
									objectTable: 'transaction_summary',
								},
							},
							styleConfig: {
								size: 'xs',
								colorRule: {
									type: 'value-mapping',
								},
								valueMapping: TRANSACTION_STATUS_MAPPING,
							},
							displayStyle: 'badge',
						},
						{
							id: 'line-break-failed-attempts',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'line_break',
						},
						{
							id: 'failed-attempts-field',
							// PD-15091 — a declined refund/void is otherwise invisible
							// here: the summary status stays Succeeded and the net
							// amount does not move. Renders nothing when the chain has
							// no failed attempts.
							dataConfig: {
								type: 'conditional',
								conditions: [
									{
										field: 'failedAttemptCount',
										operator: 'not_equals',
										value: '0',
										dataConfig: {
											type: 'template',
											template: '{failedAttemptCount} failed refund attempt(s)',
											templateFields: [{ fieldName: 'failedAttemptCount' }],
										},
									},
								],
								defaultDataConfig: {
									type: 'custom_text',
									customText: '',
								},
							},
							styleConfig: {
								size: 'xs',
								colorRule: {
									type: 'static',
									staticColor: {
										text: '#dc2626',
									},
								},
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h6', text: 'Status' }],
					minWidth: 120,
					sortable: true,
					alignment: 'center',
					sortFields: [
						{
							direction: 'ASC',
							fieldName: 'm_payment_status',
							objectTable: 'transaction_summary',
						},
					],
					widthConfig: {
						mode: 'flexible',
						preset: 'small',
					},
					verticalAlignment: 'center',
				},
				{
					id: 'actions',
					fields: [
						{
							id: 'actions-dropdown',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'actions_dropdown',
							dropdownActions: [
								{
									id: 'void-action',
									label: 'Void',
									iconName: 'ban',
									callbackName: 'void',
									variant: 'danger',
									visibility: 'visible',
									visibilityCallback: 'shouldShowVoidButton',
								},
								{
									id: 'refund-action',
									label: 'Refund',
									iconName: 'rotate-left',
									callbackName: 'refund',
									variant: 'warning',
									visibility: 'visible',
									visibilityCallback: 'shouldShowRefundButton',
								},
							],
						},
					],
					headers: [{ id: 'h7', text: '' }],
					width: 80,
					align: 'center',
					sortable: false,
				},
			],
			description: 'Order transactions - embedded in order detail view',
			defaultSortId: 'column-transaction-dates-desc',
			requiredFields: [
				'transaction_summary._id',
				'transaction_summary.m_payment_datetime',
				'transaction_summary.m_payment_netAmount',
				'transaction_summary.m_payment_origin',
				'transaction_summary.m_payment_payment_method_name',
				'transaction_summary.m_payment_payment_method_type',
				'transaction_summary.m_payment_status',
				'transaction_summary.m_sm_payment_amount',
			],
		},
	],
	paging: {
		defaultPageSize: 20,
		pageSizeOptions: [20, 50, 100],
	},
	showResetButton: true,
	filters: {
		activeFilters: [],
		defaultFilters: {},
	},
	objects: [
		{
			baseUrl: '/transactions/:id',
			objectLabel: 'Transaction',
			objectTable: 'transaction_summary',
			selectedFields: [
				{ fieldName: '_id', fieldLabel: 'ID' },
				{ fieldName: 'm_sm_payment_amount', fieldLabel: 'Payment Amount' },
				{ fieldName: 'm_payment_netAmount', fieldLabel: 'Net Amount' },
				{ fieldName: 'm_payment_status', fieldLabel: 'Status' },
				{ fieldName: 'm_payment_origin', fieldLabel: 'Origin' },
				{ fieldName: 'm_payment_datetime', fieldLabel: 'Payment Date' },
				{
					fieldName: 'm_payment_payment_method_type',
					fieldLabel: 'Payment Method Type',
				},
				{
					fieldName: 'm_payment_payment_method_name',
					fieldLabel: 'Payment Method Name',
				},
				// PD-14977 — these were absent from selectedFields entirely, so
				// they could not even be added to this tab from the column
				// picker. Without the type and the settled flag, an
				// authorization and a capture are indistinguishable here.
				{ fieldName: 'transactionType', fieldLabel: 'Transaction Type' },
				{ fieldName: 'isSettled', fieldLabel: 'Funds Collected' },
				{ fieldName: 'resultText', fieldLabel: 'Gateway Result' },
				{ fieldName: 'refundedAmount', fieldLabel: 'Refunded Amount' },
				{
					fieldName: 'failedAttemptCount',
					fieldLabel: 'Failed Refund Attempts',
				},
				{ fieldName: 'orderId', fieldLabel: 'Order ID' },
				{ fieldName: 'invoiceId', fieldLabel: 'Invoice ID' },
			],
		},
	],
	tableSorts: [
		{
			id: 'date-desc',
			name: 'Date (Newest First)',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'm_payment_datetime',
					objectTable: 'transaction_summary',
				},
			],
			description: 'Sort transactions by date, newest first',
		},
		{
			id: 'date-asc',
			name: 'Date (Oldest First)',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'm_payment_datetime',
					objectTable: 'transaction_summary',
				},
			],
			description: 'Sort transactions by date, oldest first',
		},
		{
			id: 'amount-desc',
			name: 'Amount (Highest First)',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'm_sm_payment_amount',
					objectTable: 'transaction_summary',
				},
			],
			description: 'Sort transactions by amount, highest first',
		},
	],
};
